import { fetchEventSource } from '@microsoft/fetch-event-source';
import { authorizedFetch } from '../auth/authorizedFetch';
import { getAccessToken } from '../auth/tokenStore';
import { BASE_URL } from 'api/config';

export type ResyncPayload = { kind: 'resync'; execution_id?: string };

class StreamUnavailableError extends Error {}
class StreamFatalError extends Error {}

const MAX_CONSECUTIVE_FAILURES = 5;
const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 10_000;

export type SubscribeOptions<Snapshot, Event> = {
  onSnapshot: (snapshot: Snapshot) => void;
  onEvent: (event: Event) => void;
  onResync?: (event: ResyncPayload) => void;
  onUnavailable?: () => void;
  onFatal?: (error: unknown) => void;
  reconnectOnClose?: boolean;
};

export function subscribe<Snapshot extends { kind: 'snapshot' }, Event extends { kind: string }>(
  path: string,
  options: SubscribeOptions<Snapshot, Event>,
): () => void {
  const controller = new AbortController();
  let consecutiveFailures = 0;

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  fetchEventSource(url, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
    },
    fetch: authorizedFetch,
    signal: controller.signal,
    openWhenHidden: true,
    onopen(response) {
      if (response.status === 503) {
        options.onUnavailable?.();
        throw new StreamUnavailableError('Realtime streaming disabled');
      }

      if (response.status === 401) {
        const error = new StreamFatalError('Unauthenticated');
        options.onFatal?.(error);
        throw error;
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (!response.ok || !contentType.toLowerCase().startsWith('text/event-stream')) {
        throw new Error(`Unexpected SSE response: ${response.status} ${contentType}`);
      }

      consecutiveFailures = 0;
      return Promise.resolve();
    },
    onmessage(message) {
      if (!message.data) return;

      let parsed: { kind?: string };
      try {
        parsed = JSON.parse(message.data) as { kind?: string };
      } catch (error) {
        console.warn('SSE parse error', error, message.data);
        return;
      }

      if (parsed.kind === 'snapshot') {
        options.onSnapshot(parsed as Snapshot);
      } else if (parsed.kind === 'resync') {
        options.onResync?.(parsed as ResyncPayload);
      } else if (parsed.kind) {
        options.onEvent(parsed as Event);
      }
    },
    onclose() {
      if (options.reconnectOnClose === false) {
        throw new StreamFatalError('closed');
      }
    },
    onerror(error) {
      if (error instanceof StreamUnavailableError || error instanceof StreamFatalError) {
        throw error;
      }

      if (!getAccessToken()) {
        options.onFatal?.(error);
        throw new StreamFatalError('Unauthenticated');
      }

      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        options.onFatal?.(error);
        throw new StreamFatalError('max retries exceeded');
      }

      return Math.min(BASE_RETRY_MS * 2 ** (consecutiveFailures - 1), MAX_RETRY_MS);
    },
  }).catch((error: unknown) => {
    if (error instanceof StreamUnavailableError || error instanceof StreamFatalError) return;
    if ((error as { name?: string } | null)?.name === 'AbortError') return;

    options.onFatal?.(error);
  });

  return () => controller.abort();
}
